import { Link } from "react-router-dom";

const STATS = [
  { value: "7", label: "Core compliance modules" },
  { value: "1", label: "Unified system of record" },
  { value: "AI", label: "Policy & risk assistance" },
  { value: "360°", label: "Data-to-decision visibility" },
];

const ICON_COLORS = {
  teal: "bg-[#E6F7F3] text-[#0B8F7A]",
  gold: "bg-[#F8F0DC] text-[#B8862E]",
  blue: "bg-[#E8F0FE] text-[#2B6CB0]",
  violet: "bg-[#F0E8FF] text-[#6B46C1]",
  coral: "bg-[#FFECE8] text-[#C2410C]",
  emerald: "bg-[#E6F6EC] text-[#15803D]",
  indigo: "bg-[#EEF0FF] text-[#4338CA]",
  amber: "bg-[#FEF3C7] text-[#B45309]",
};

const CARDS = [
  {
    icon: "bi-layers",
    color: "gold",
    title: "Unified Interface",
    text: "One workspace for privacy, legal, security and business — shared records, approvals and audit trails across every module.",
    featured: true,
  },
  {
    icon: "bi-toggle2-on",
    color: "teal",
    title: "Consent Management",
    text: "Capture, honour and prove consent across channels — purpose-bound, withdrawable and audit-ready under DPDP.",
    href: "/consent-management",
  },
  {
    icon: "bi-person-bounding-box",
    color: "blue",
    title: "DSAR",
    text: "Intake, verify and fulfil Data Principal rights — access, correction, erasure — with tracked SLAs.",
  },
  {
    icon: "bi-clipboard2-check",
    color: "emerald",
    title: "Gap Assessment",
    text: "Benchmark controls against the DPDP Act and turn findings into prioritised, owned remediation plans.",
  },
  {
    icon: "bi-shield-exclamation",
    color: "violet",
    title: "PIA",
    text: "Structured Privacy Impact Assessments with risk scoring, mitigations and multi-party sign-off.",
  },
  {
    icon: "bi-journal-richtext",
    color: "indigo",
    title: "ROPA",
    text: "Living Record of Processing Activities linked to systems, vendors, purposes and retention rules.",
  },
  {
    icon: "bi-buildings",
    color: "amber",
    title: "TPRM",
    text: "Onboard, assess and continuously monitor processors handling personal data on your behalf.",
  },
  {
    icon: "bi-exclamation-octagon",
    color: "coral",
    title: "Breach Management",
    text: "Triage incidents, run statutory clocks and produce a defensible, timestamped response trail.",
  },
];

function PlatCard({ icon, color, title, text, featured, href }) {
  const content = (
    <>
      <div
        className={`w-12 h-12 rounded-xl flex items-center justify-center text-[1.3rem] mb-[1.15rem] ${
          featured ? "bg-gold/20 text-gold-bright" : ICON_COLORS[color]
        }`}
      >
        <i className={`bi ${icon}`} />
      </div>
      <h3 className="text-[1.12rem] font-bricolage font-semibold mb-2">
        {title}
      </h3>
      <p
        className={`text-[0.92rem] leading-[1.55] flex-1 ${
          featured ? "text-white/[.58]" : "text-slate"
        }`}
      >
        {text}
      </p>
    </>
  );

  const baseClass = `group relative flex flex-col h-full p-[1.55rem_1.45rem_1.4rem] rounded-[10px] border overflow-hidden transition-all duration-[250ms] hover:-translate-y-[5px] ${
    featured
      ? "text-white border-gold/[.35] hover:border-gold hover:shadow-[0_18px_40px_rgba(7,17,31,0.25)]"
      : "bg-surface border-line hover:border-gold/[.55] hover:shadow-[0_18px_40px_rgba(7,17,31,0.08)]"
  }`;

  const style = featured
    ? { background: "linear-gradient(160deg, #0E1A2B 0%, #12283A 100%)" }
    : undefined;

  const topBar = (
    <span
      className="absolute top-0 left-0 right-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-[250ms]"
      style={{ background: "linear-gradient(90deg, #C2A478, #12B89A)" }}
    />
  );

  if (href) {
    return (
      <Link to={href} className={baseClass} style={style}>
        {topBar}
        {content}
      </Link>
    );
  }

  return (
    <div className={baseClass} style={style}>
      {topBar}
      {content}
    </div>
  );
}

export default function Platform() {
  return (
    <section
      id="platform"
      className="py-[5.5rem] bg-white border-b border-line relative"
      style={{
        background:
          "radial-gradient(ellipse 60% 40% at 10% 0%, rgba(201, 164, 92, 0.08), transparent 55%), radial-gradient(ellipse 50% 35% at 90% 10%, rgba(11, 143, 122, 0.08), transparent 50%), #FFFFFF",
      }}
    >
      <div className="container mx-auto max-w-[1140px] px-4">
        {/* banner */}
        <div
          className="relative overflow-hidden grid grid-cols-1 md:grid-cols-[1.15fr_1fr] gap-8 items-center p-7 md:p-[2.25rem_2.4rem] rounded-[10px] text-white mb-8"
          style={{
            background:
              "linear-gradient(135deg, #0A1628 0%, #12253A 55%, #0E1F30 100%)",
          }}
        >
          <div
            className="absolute -right-[8%] -top-[40%] w-[340px] h-[340px] rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(201, 164, 92, 0.22), transparent 65%)",
            }}
          />
          <div>
            <p className="inline-block text-[0.78rem] font-semibold tracking-[0.12em] uppercase text-gold-bright mb-[0.6rem]">
              One Platform
            </p>
            <h2 className="font-bricolage text-[clamp(1.7rem,2.8vw,2.3rem)] text-white mb-[0.7rem]">
              Unified Interface for DPDP
            </h2>
            <p className="m-0 text-white/[.62] text-[1.02rem] leading-[1.6] max-w-[34rem]">
              Everything your privacy, legal and security teams need — consent,
              rights requests, assessments, processing records, vendor risk and
              breach response — in a single connected console. One login. One
              workflow engine. One source of truth.
            </p>
          </div>
          <div className="relative z-[1] grid grid-cols-2 gap-[0.85rem]">
            {STATS.map((s) => (
              <div
                key={s.label}
                className="p-[1.1rem_1.15rem] rounded-lg bg-white/[.05] border border-white/10"
              >
                <strong className="block font-bricolage text-[1.55rem] text-gold-bright leading-[1.1] mb-1">
                  {s.value}
                </strong>
                <span className="text-[0.82rem] text-white/[.55]">
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* card grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[1.15rem]">
          {CARDS.map((c) => (
            <PlatCard key={c.title} {...c} />
          ))}
        </div>
      </div>
    </section>
  );
}
